// =============================================================================
// FILE: packages/renderer/src/stores/projectContext.ts
//
// ARCHITECTURAL RULE
//   ProjectContext is the ONLY way to resolve the "active project" in UI.
//   Every consumer that needs the active project's clientTimezone, name,
//   id, etc. MUST read it through `useActiveProject()`. Direct DB reads
//   in components are forbidden — they cause drift the moment AutoPilot,
//   Calendar, or Email start relying on the same field.
//
//   The store holds only the active *id*. The full ProjectRow is resolved
//   live through useProject() so cache invalidation flows naturally
//   through React Query.
// =============================================================================

import { useMemo } from 'react';
import { useSyncExternalStore } from 'react';
import { useProjects } from '../hooks/useProjects.js';
import { TimezoneService } from '../services/TimezoneService.js';
import type { ProjectRow } from '../hooks/useProjects.js';
import { touchPersistentProject } from '../lib/localProjectPersistence.js';

const ACTIVE_PROJECT_KEY = 'projectContext.activeProjectId';

interface ProjectContextState { activeProjectId: string | null }

const subs = new Set<() => void>();
let state: ProjectContextState = {
  activeProjectId: typeof window === 'undefined'
    ? null
    : localStorage.getItem(ACTIVE_PROJECT_KEY),
};
function notify(): void { for (const s of subs) s(); }

export const projectContextStore = {
  getState: (): ProjectContextState => state,
  setActiveProject(id: string | null): void {
    state = { activeProjectId: id };
    if (id) localStorage.setItem(ACTIVE_PROJECT_KEY, id);
    else    localStorage.removeItem(ACTIVE_PROJECT_KEY);
    if (id) void touchPersistentProject(id).catch(() => undefined);
    try { window.dispatchEvent(new CustomEvent('r2:project-active-changed', { detail: { projectId: id } })); } catch { /* no-op outside browser */ }
    notify();
  },
  subscribe(fn: () => void): () => void { subs.add(fn); return () => subs.delete(fn); },
};

function useProjectContextStore(): ProjectContextState {
  return useSyncExternalStore(projectContextStore.subscribe,
                              projectContextStore.getState,
                              projectContextStore.getState);
}

export interface ActiveProject {
  /** Active project id, may be null until the user picks one. */
  id:              string | null;
  row:             ProjectRow | null;
  /** Always resolves to a valid IANA tz — falls back to local browser tz
   *  when the project has no clientTimezone set yet. Consumers should
   *  treat this as the authoritative answer for ALL timezone math. */
  clientTimezone:  string;
  /** True when the project has its own clientTimezone (vs. falling back
   *  to the local browser). UI can render an "unset" hint accordingly. */
  hasExplicitTz:   boolean;
  setActiveProject: (id: string | null) => void;
  /** List of projects suitable for an Active Project picker. */
  available:       ProjectRow[];
  isLoading:       boolean;
}

/** Hook — single resolver consumed by Dashboard clocks, Calendar Engine
 *  bridges, future Reminders, etc. */
export function useActiveProject(): ActiveProject {
  const { activeProjectId } = useProjectContextStore();
  const projects = useProjects(1, 200);

  const row = useMemo(() => {
    const list = projects.data?.items ?? [];
    if (!activeProjectId) return null;
    return list.find((p) => p.id === activeProjectId) ?? null;
  }, [activeProjectId, projects.data]);

  const explicitTz = row?.clientTimezone && TimezoneService.isValidTimeZone(row.clientTimezone)
    ? row.clientTimezone : null;
  const clientTimezone = explicitTz ?? TimezoneService.getLocalTimezone();

  return {
    id: activeProjectId,
    row,
    clientTimezone,
    hasExplicitTz: !!explicitTz,
    setActiveProject: projectContextStore.setActiveProject,
    available: projects.data?.items ?? [],
    isLoading: projects.isLoading,
  };
}
